import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Trash2, Clock, Lock, Save, ChevronLeft, ChevronRight, Globe, Bookmark, Heart, Sparkles } from "lucide-react";
import { ShareToExploreDialog } from "@/components/ShareToExploreDialog";
import type { User } from "@supabase/supabase-js";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectCardSkeleton } from "@/components/ProjectCardSkeleton";
import { trackProjectsPageViewed, trackProjectCardClicked, trackProjectDeleted } from "@/lib/analytics";
import { getRetentionConfig } from "@/lib/retention";
import { isNativePlatform } from "@/lib/platform";

const PAGE_SIZE = 6;

type FilterType = "all" | "auto-saved" | "explicit" | "expiring";
type ViewMode = "my-designs" | "saved-explore";

interface SavedExploreDesign {
  id: string;
  design_id: string;
  created_at: string;
  saved_designs: {
    id: string;
    transformed_image_url: string | null;
    original_image_url: string;
    design_style: string;
    room_type: string;
    designer_note: string | null;
    profiles: { username: string } | null;
  } | null;
}

interface SavedDesign {
  id: string;
  title: string;
  original_image_url: string;
  transformed_image_url: string | null;
  all_variations?: string[] | null;
  public_variations?: string[] | null;
  room_type: string;
  design_style: string;
  detected_room_type: string | null;
  created_at: string;
  is_auto_saved?: boolean;
  saved_permanently?: boolean;
  expires_at?: string | null;
  retention_policy?: string;
  is_public?: boolean;
  designer_note?: string | null;
}

export default function Projects() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [designs, setDesigns] = useState<SavedDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>("all");
  const [userTier, setUserTier] = useState<"free" | "pro" | "business" | "enterprise">("free");
  const [expiringCount, setExpiringCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [shareDialogId, setShareDialogId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("my-designs");
  const [savedExploreDesigns, setSavedExploreDesigns] = useState<SavedExploreDesign[]>([]);
  const [loadingSavedExplore, setLoadingSavedExplore] = useState(false);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
      loadDesigns(session.user.id, 1, "all");
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUser(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const loadDesigns = useCallback(async (userId: string, page: number, activeFilter: FilterType) => {
    try {
      setLoading(true);

      // Fetch user tier
      const { data: profileData } = await supabase
        .from("profiles")
        .select("subscription_tier")
        .eq("id", userId)
        .single();

      if (profileData) {
        setUserTier(profileData.subscription_tier);
      }

      // Calculate offset
      const offset = (page - 1) * PAGE_SIZE;

      // Build base query
      let query = supabase
        .from("saved_designs")
        .select(
          "id, title, original_image_url, transformed_image_url, all_variations, public_variations, room_type, design_style, detected_room_type, created_at, is_auto_saved, saved_permanently, expires_at, is_public, designer_note",
          { count: "exact" },
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      // Apply filter conditions
      const now = new Date().toISOString();
      const threeDaysFromNow = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();

      if (activeFilter === "auto-saved") {
        query = query.eq("is_auto_saved", true);
      } else if (activeFilter === "explicit") {
        query = query.eq("is_auto_saved", false);
      } else if (activeFilter === "expiring") {
        query = query.lt("expires_at", threeDaysFromNow).gte("expires_at", now);
      }

      // Apply pagination
      const { data, error, count } = await query.range(offset, offset + PAGE_SIZE - 1);

      if (error) throw error;

      setDesigns((data || []) as SavedDesign[]);
      setTotalCount(count || 0);

      // Fetch expiring count separately (lightweight query) - only for 'all' filter
      if (activeFilter === "all") {
        const { count: expCount } = await supabase
          .from("saved_designs")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .lt("expires_at", threeDaysFromNow)
          .gte("expires_at", now);

        setExpiringCount(expCount || 0);
      }

      // Track projects page view
      trackProjectsPageViewed(count || 0, profileData?.subscription_tier || "free");
    } catch (error: any) {
      const isTimeout = error?.code === "57014" || error?.message?.includes("timeout");
      toast({
        variant: "destructive",
        title: isTimeout ? "Loading timeout" : "Error",
        description: isTimeout
          ? "Projects are taking longer than expected to load. Please try again."
          : "Failed to load designs",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setCurrentPage(1);
    if (user) {
      loadDesigns(user.id, 1, newFilter);
    }
  };

  const loadSavedExploreDesigns = useCallback(async (userId: string) => {
    setLoadingSavedExplore(true);
    try {
      const { data } = await supabase
        .from("explore_saves")
        .select("id, design_id, created_at, saved_designs!explore_saves_design_id_fkey(id, transformed_image_url, original_image_url, design_style, room_type, designer_note, profiles!saved_designs_user_id_fkey(username))")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      setSavedExploreDesigns((data || []) as unknown as SavedExploreDesign[]);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load saved designs" });
    } finally {
      setLoadingSavedExplore(false);
    }
  }, []);

  const handleUnsaveExplore = async (saveId: string) => {
    await supabase.from("explore_saves").delete().eq("id", saveId);
    setSavedExploreDesigns((prev) => prev.filter((d) => d.id !== saveId));
    toast({ title: "Removed", description: "Design removed from saves" });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || !user) return;
    setCurrentPage(newPage);
    loadDesigns(user.id, newPage, filter);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (designId: string) => {
    try {
      const { error } = await supabase.from("saved_designs").delete().eq("id", designId);

      if (error) throw error;

      // Update expiring count if needed
      const deletedDesign = designs.find((d) => d.id === designId);
      if (deletedDesign?.expires_at) {
        const days = getDaysUntilExpiry(deletedDesign.expires_at);
        if (days !== null && days <= 3) {
          setExpiringCount((prev) => Math.max(0, prev - 1));
        }
      }

      trackProjectDeleted(designId, userTier);
      toast({
        title: "Deleted",
        description: "Design removed successfully",
      });

      // Reload current page to reflect changes
      // If we deleted the last item on the page and it's not page 1, go back a page
      if (designs.length === 1 && currentPage > 1) {
        handlePageChange(currentPage - 1);
      } else if (user) {
        loadDesigns(user.id, currentPage, filter);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete design",
      });
    }
  };

  const handleConvertToExplicit = async (designId: string) => {
    // Block free tier users from saving - redirect to pricing
    if (userTier === "free") {
      toast({
        variant: "destructive",
        title: "Paid subscription required",
        description: "Upgrade to Pro or Business to save projects and extend retention.",
      });
      navigate("/pricing");
      return;
    }

    try {
      const retention = getRetentionConfig(userTier, false);

      const { error } = await supabase
        .from("saved_designs")
        .update({
          is_auto_saved: false,
          saved_permanently: retention.savedPermanently,
          retention_policy: retention.retentionPolicy,
          expires_at: retention.expiresAt,
        })
        .eq("id", designId);

      if (error) throw error;

      // Update local state
      setDesigns((prev) =>
        prev.map((d) =>
          d.id === designId
            ? {
                ...d,
                is_auto_saved: false,
                saved_permanently: retention.savedPermanently,
                expires_at: retention.expiresAt,
              }
            : d,
        ),
      );

      toast({
        title: "Saved!",
        description: retention.displayText,
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save project",
      });
    }
  };

  const getDaysUntilExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const days = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("ellipsis");
      }

      // Show pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Skeleton loading state
  if (loading) {
    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-background via-background to-primary/5 px-4 pb-4 md:p-8 ${isNativePlatform() ? "pt-safe-header-compact safe-area-inset-bottom" : "pt-2"}`}
      >
        <div className="max-w-7xl mx-auto">
          {/* Header skeleton */}
          <div className="flex flex-col gap-2 mb-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-24" />
              <Skeleton className="h-8 w-40" />
            </div>
            <Skeleton className="h-5 w-64 hidden sm:block" />
          </div>

          {/* Tabs skeleton */}
          <Skeleton className="h-10 w-full md:w-96 mb-4 md:mb-6" />

          {/* Grid of skeleton cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <ProjectCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-background via-background to-primary/5 px-4 pb-4 md:p-8 ${isNativePlatform() ? "pt-safe-header-compact safe-area-inset-bottom" : "pt-2"}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header - Responsive */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/designer")} variant="outline" size="sm">
              <ArrowLeft className="mr-1 h-4 w-4" />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back to Designer</span>
            </Button>
            <h1 className="text-xl sm:text-2xl md:text-4xl font-bold">My Projects</h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground hidden sm:block">
            View and manage your saved designs
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={viewMode === "my-designs" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("my-designs")}
          >
            My Designs
          </Button>
          <Button
            variant={viewMode === "saved-explore" ? "default" : "outline"}
            size="sm"
            className="gap-1.5"
            onClick={() => {
              setViewMode("saved-explore");
              if (user) loadSavedExploreDesigns(user.id);
            }}
          >
            <Bookmark className="h-3.5 w-3.5" />
            Saved from Explore
          </Button>
        </div>
        {viewMode === "saved-explore" ? (
          /* Saved from Explore View */
          loadingSavedExplore ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <ProjectCardSkeleton key={i} />
              ))}
            </div>
          ) : savedExploreDesigns.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 md:py-16">
                <Bookmark className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="text-base md:text-lg text-muted-foreground mb-4">No saved designs yet</p>
                <Button onClick={() => navigate("/explore")}>Browse Explore</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {savedExploreDesigns.map((save) => {
                const d = save.saved_designs;
                if (!d) return null;
                const imgUrl = d.transformed_image_url || d.original_image_url;
                return (
                  <Card key={save.id} className="overflow-hidden group">
                    <div className="relative aspect-square md:aspect-video bg-muted">
                      <img src={imgUrl} alt={`${d.design_style} ${d.room_type}`} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <CardContent className="p-2.5 md:p-4">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <Badge variant="secondary" className="text-[10px] md:text-xs">{d.design_style}</Badge>
                        <Badge variant="outline" className="text-[10px] md:text-xs">{d.room_type}</Badge>
                      </div>
                      <p className="text-[10px] md:text-xs text-muted-foreground mb-2">
                        by @{d.profiles?.username || "anonymous"}
                      </p>
                      {d.designer_note && (
                        <p className="text-[10px] md:text-xs text-muted-foreground italic mb-2 line-clamp-2">"{d.designer_note}"</p>
                      )}
                      <div className="flex gap-1.5 flex-wrap">
                        <Button
                          variant="default"
                          size="sm"
                          className="h-7 md:h-9 text-[10px] md:text-sm gap-1"
                          onClick={() => navigate(`/designer?style=${encodeURIComponent(d.design_style)}&room=${encodeURIComponent(d.room_type)}`)}
                        >
                          <Sparkles className="h-3 w-3" />
                          Try This Style
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 md:h-9 text-[10px] md:text-sm gap-1"
                          onClick={() => handleUnsaveExplore(save.id)}
                        >
                          <Bookmark className="h-3 w-3 fill-primary text-primary" />
                          Unsave
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        ) : (
          <>
        {expiringCount > 0 && filter !== "expiring" && (
          <Card className="mb-4 md:mb-6 border-orange-500/50 bg-orange-500/10">
            <CardContent className="p-3 md:p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 md:h-5 md:w-5 text-orange-500" />
                <span className="text-sm md:text-base font-medium">
                  ⚠️ {expiringCount} project{expiringCount !== 1 ? "s" : ""} expiring soon
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleFilterChange("expiring")}>
                View
              </Button>
            </CardContent>
          </Card>
        )}

        {totalCount === 0 && filter === "all" ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 md:py-16">
              <p className="text-base md:text-lg text-muted-foreground mb-4">No saved designs yet</p>
              <Button onClick={() => navigate("/designer")}>Start Designing</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Tabs - Scrollable on mobile */}
            <Tabs value={filter} onValueChange={(v) => handleFilterChange(v as FilterType)} className="mb-4 md:mb-6">
              <TabsList className="w-full md:w-auto overflow-x-auto flex-nowrap justify-start">
                <TabsTrigger value="all" className="text-xs md:text-sm whitespace-nowrap">
                  All
                </TabsTrigger>
                <TabsTrigger value="auto-saved" className="text-xs md:text-sm whitespace-nowrap">
                  Auto Saved
                </TabsTrigger>
                <TabsTrigger value="explicit" className="text-xs md:text-sm whitespace-nowrap">
                  Explicitly Saved
                </TabsTrigger>
                <TabsTrigger value="expiring" className="text-xs md:text-sm whitespace-nowrap">
                  Expiring Soon
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {designs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 md:py-16">
                  <p className="text-base md:text-lg text-muted-foreground mb-4">No projects match this filter</p>
                  <Button variant="outline" onClick={() => handleFilterChange("all")}>
                    View All Projects
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Grid - 2 columns on mobile, 3 on desktop */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                  {designs.map((design) => {
                    const daysUntilExpiry = getDaysUntilExpiry(design.expires_at || null);
                    const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 3;
                    const isAutoSaved = design.is_auto_saved === true;

                    return (
                      <Card
                        key={design.id}
                        className={`overflow-hidden group ${isAutoSaved ? "opacity-90" : ""} ${isExpiringSoon ? "border-orange-500/50" : ""}`}
                      >
                        {/* Image - Square aspect on mobile */}
                        <div className="relative aspect-square md:aspect-video bg-muted">
                          <img
                            src={design.transformed_image_url || design.original_image_url}
                            alt={design.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          {isAutoSaved && (
                            <Badge className="absolute top-1.5 left-1.5 md:top-2 md:left-2 bg-muted-foreground/80 text-[10px] md:text-xs px-1.5 md:px-2 py-0.5">
                              <Clock className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" />
                              Auto saved
                            </Badge>
                          )}
                          {design.saved_permanently && (
                            <Badge className="absolute top-1.5 right-1.5 md:top-2 md:right-2 bg-primary text-[10px] md:text-xs px-1.5 md:px-2 py-0.5">
                              <Lock className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" />
                              Permanent
                            </Badge>
                          )}
                        </div>
                        <CardContent className="p-2.5 md:p-4">
                          {/* Title and info - smaller on mobile */}
                          <h3 className="font-semibold text-xs md:text-base mb-1 md:mb-2 truncate">{design.title}</h3>
                          <div className="hidden md:flex items-center justify-between text-sm text-muted-foreground mb-2">
                            <span className="truncate">{design.design_style}</span>
                            <span className="truncate">{design.room_type}</span>
                          </div>

                          {/* Mobile: Show date */}
                          <p className="md:hidden text-[10px] text-muted-foreground mb-1.5">
                            {new Date(design.created_at).toLocaleDateString()}
                          </p>

                          {daysUntilExpiry !== null && !design.saved_permanently && (
                            <div
                              className={`text-[10px] md:text-xs mb-2 md:mb-3 flex items-center gap-1 ${isExpiringSoon ? "text-orange-500 font-medium" : "text-muted-foreground"}`}
                            >
                              <Clock className="h-2.5 w-2.5 md:h-3 md:w-3" />
                              {daysUntilExpiry === 0 ? "Expires today" : `${daysUntilExpiry}d left`}
                            </div>
                          )}

                          {/* Action Buttons - Compact on mobile */}
                          <div className="flex gap-1.5 md:gap-2 overflow-x-auto">
                            {isAutoSaved && (
                              <Button
                                variant={userTier === "free" ? "outline" : "default"}
                                size="sm"
                                className="shrink-0 h-7 md:h-9 text-[10px] md:text-sm px-1.5 md:px-3"
                                onClick={() => handleConvertToExplicit(design.id)}
                              >
                                <Save className="h-3 w-3 md:mr-1" />
                                <span className="hidden md:inline">
                                  {userTier === "free" ? "Upgrade to save" : "Save"}
                                </span>
                                <span className="md:hidden">{userTier === "free" ? "Upgrade" : "Save"}</span>
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 h-7 md:h-9 text-[10px] md:text-sm px-2 md:px-3"
                              onClick={() => {
                                const ageDays = Math.floor(
                                  (Date.now() - new Date(design.created_at).getTime()) / (1000 * 60 * 60 * 24),
                                );
                                trackProjectCardClicked(design.id, ageDays);
                                navigate(`/designer?load=${design.id}`);
                              }}
                            >
                              Load
                            </Button>
                            <Button
                              variant={design.is_public ? "default" : "outline"}
                              size="sm"
                              className={`shrink-0 h-7 md:h-9 text-[10px] md:text-sm px-2 md:px-3 ${design.is_public ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
                              onClick={() => {
                                if (design.is_public) {
                                  // Unshare directly — also clear public_variations
                                  (async () => {
                                    const { error } = await supabase
                                      .from("saved_designs")
                                      .update({ is_public: false, designer_note: null, public_variations: [] })
                                      .eq("id", design.id);
                                    if (error) {
                                      toast({ variant: "destructive", title: "Error", description: "Failed to update sharing" });
                                      return;
                                    }
                                    setDesigns((prev) => prev.map((d) => d.id === design.id ? { ...d, is_public: false, designer_note: null, public_variations: [] } : d));
                                    toast({ title: "Unshared", description: "Design removed from Explore" });
                                  })();
                                } else {
                                  // Open share dialog
                                  setShareDialogId(design.id);
                                }
                              }}
                            >
                              <Globe className="h-3 w-3 md:h-4 md:w-4" />
                              <span className="hidden md:inline ml-1">{design.is_public ? "Shared" : "Share"}</span>
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="h-7 md:h-9 px-2 md:px-3 shrink-0"
                              onClick={() => handleDelete(design.id)}
                            >
                              <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-6 md:mt-8 flex items-center justify-center">
                    {/* Mobile pagination */}
                    <div className="flex md:hidden items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-9 w-9 p-0"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-9 w-9 p-0"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Desktop pagination */}
                    <div className="hidden md:flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="gap-1 pl-2.5"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>

                      {getPageNumbers().map((page, index) =>
                        page === "ellipsis" ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="h-9 w-9 p-0"
                          >
                            {page}
                          </Button>
                        ),
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="gap-1 pr-2.5"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
          </>
        )}
      </div>

      {/* Share to Explore Dialog */}
      {(() => {
        const currentDesign = designs.find((d) => d.id === shareDialogId);
        // Always show the FULL set of variations so the user can re-select freely.
        const variations =
          currentDesign?.all_variations && currentDesign.all_variations.length > 0
            ? currentDesign.all_variations
            : currentDesign?.transformed_image_url
            ? [currentDesign.transformed_image_url]
            : [];
        // Pre-select previously-shared subset (if any) when reopening.
        const initialSelectedUrls =
          currentDesign?.public_variations && currentDesign.public_variations.length > 0
            ? currentDesign.public_variations
            : undefined;
        return (
          <ShareToExploreDialog
            open={!!shareDialogId}
            onClose={() => setShareDialogId(null)}
            variations={variations}
            initialSelectedUrls={initialSelectedUrls}
            initialNote={currentDesign?.designer_note ?? ""}
            onShare={async (note, selectedUrls) => {
              if (!shareDialogId || selectedUrls.length === 0) return;
              // IMPORTANT: only write public_variations. Do NOT touch
              // all_variations or transformed_image_url — those represent the
              // user's full immutable project.
              const { error } = await supabase
                .from("saved_designs")
                .update({
                  is_public: true,
                  designer_note: note || null,
                  public_variations: selectedUrls,
                })
                .eq("id", shareDialogId);
              if (error) {
                toast({ variant: "destructive", title: "Error", description: "Failed to share" });
                return;
              }
              setDesigns((prev) =>
                prev.map((d) =>
                  d.id === shareDialogId
                    ? {
                        ...d,
                        is_public: true,
                        designer_note: note || null,
                        public_variations: selectedUrls,
                      }
                    : d,
                ),
              );
              toast({ title: "Shared!", description: "Design is now visible on Explore" });
            }}
          />
        );
      })()}
    </div>
  );
}
